import _omit from 'lodash/omit';
import { Widget, WidgetUpdateInputObject } from 'graphql-common';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';

/**
 * Grid layout item for react-grid-layout.
 * Represents position and size of a widget in the grid.
 */
export interface GridLayoutItem {
  /** Unique identifier (widget id or temp id) */
  i: string;
  /** X position in grid units */
  x: number;
  /** Y position in grid units */
  y: number;
  /** Width in grid units */
  w: number;
  /** Height in grid units */
  h: number;
  /** Minimum width constraint */
  minW?: number;
  /** Minimum height constraint */
  minH?: number;
  /** Maximum width constraint */
  maxW?: number;
  /** Maximum height constraint */
  maxH?: number;
}

/**
 * Local widget representation for editing.
 * Extends API Widget with additional fields for local state management.
 */
export interface LocalWidget {
  /** ID for existing widgets (from API) */
  id?: string;
  /** Temporary ID for new widgets (not yet saved) */
  tempId?: string;
  /** Widget type (e.g., 'Clock', 'Weather', 'Announcement') */
  kind: string;
  /** Widget title (for widgets that support it) */
  title?: string | null;
  /** Widget description/content */
  description?: string | null;
  /** Reference to widget template */
  widgetTemplateId?: string | null;
  /** Widget-specific configuration fields */
  fields?: Record<string, unknown>;
  /** Attached files (e.g., images for Announcement) */
  attachmentsAttached?: unknown[];
  /** Position in the widget list */
  position?: number;
  /** Flag to mark widget for deletion (maps to _destroy in API) */
  isDestroyed?: boolean;
  /** Raw form values - single source of truth for edited widgets */
  formValues?: Record<string, unknown>;
  /** Original widget data from API (for change detection) */
  originalWidget?: Widget;
}

/**
 * Removes null/undefined values from an object recursively.
 * For nested objects, also removes null values and removes
 * the entire nested object if it becomes empty after cleaning.
 *
 * @param obj - Object to clean
 * @returns New object with null/undefined values removed
 *
 * @example
 * removeNullValues({ a: 1, b: null, c: { d: undefined, e: 2 } })
 * // Returns: { a: 1, c: { e: 2 } }
 */
export function removeNullValues(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const cleaned = removeNullValues(value as Record<string, unknown>);
      // Only include nested object if it has content after cleaning
      if (Object.keys(cleaned).length > 0) {
        result[key] = cleaned;
      }
    } else {
      result[key] = value;
    }
  });

  return result;
}

/**
 * Converts an API Widget to LocalWidget for local editing.
 * Preserves the original widget reference for change detection.
 *
 * @param widget - Widget from API
 * @returns LocalWidget ready for editing
 */
export function widgetToLocalWidget(widget: Widget): LocalWidget {
  return {
    id: widget.id,
    kind: widget.kind,
    title: widget.title,
    description: widget.description,
    widgetTemplateId: widget.widgetTemplateId,
    fields: widget.fields as Record<string, unknown>,
    attachmentsAttached: widget.attachmentsAttached,
    position: widget.position ?? undefined,
    // Store original widget for attachment change detection
    originalWidget: widget,
  };
}

/**
 * Converts a LocalWidget to WidgetUpdateInputObject for API mutation.
 * Handles different cases: destroyed widgets, new widgets, and existing widgets.
 *
 * @param widget - LocalWidget to convert
 * @param position - Position in the widget list
 * @param gridItem - Optional grid layout item for size/position
 * @param wasEdited - Whether the widget was edited (affects attachment handling)
 * @returns WidgetUpdateInputObject ready for API
 */
export function localWidgetToUpdateInput(
  widget: LocalWidget,
  position: number,
  gridItem?: GridLayoutItem,
  wasEdited = false,
): WidgetUpdateInputObject {
  // For destroyed widgets, only send id and _destroy
  if (widget.isDestroyed && widget.id) {
    return {
      id: widget.id,
      // eslint-disable-next-line no-underscore-dangle
      _destroy: true,
    };
  }

  const isNewWidget = !widget.id;

  const input: WidgetUpdateInputObject = {
    position,
  };

  // Only include kind and widgetTemplateId for new widgets (cannot be changed for existing)
  if (isNewWidget) {
    input.kind = widget.kind as WidgetUpdateInputObject['kind'];
    if (widget.widgetTemplateId) {
      input.widgetTemplateId = widget.widgetTemplateId;
    }
  } else {
    input.id = widget.id;
  }

  if (widget.title) {
    input.title = widget.title;
  }
  if (widget.description) {
    input.description = widget.description;
  }

  // Process fields - remove __typename and null values
  if (widget.fields && Object.keys(widget.fields).length > 0) {
    const fieldsWithoutTypename = removeTypenameDeep(widget.fields) as Record<
      string,
      unknown
    >;
    const cleanedFields = removeNullValues(fieldsWithoutTypename);
    if (Object.keys(cleanedFields).length > 0) {
      input.fields = cleanedFields as WidgetUpdateInputObject['fields'];
    }
  }

  // Include attachments if:
  // 1. Widget was edited (wasEdited=true), OR
  // 2. It's a new widget (no id) that has attachments from template copy
  // For non-edited EXISTING widgets, skip attachments to avoid sending fields the API doesn't accept
  // (like fileData, processed, urls from existing attachments)
  if (
    (wasEdited || isNewWidget) &&
    widget.attachmentsAttached &&
    widget.attachmentsAttached.length > 0
  ) {
    // Strip 'url' field from attachments - it's only used for local preview
    input.attachmentsAttached = widget.attachmentsAttached.map((attachment) =>
      _omit(attachment as Record<string, unknown>, 'url'),
    ) as WidgetUpdateInputObject['attachmentsAttached'];
  }

  // Include grid data for each widget
  if (gridItem) {
    input.grid = {
      x: gridItem.x,
      y: gridItem.y,
      w: gridItem.w,
      h: gridItem.h,
    };
  }

  return input;
}

/**
 * Creates a position map from grid layout items.
 * Positions are calculated based on y position first, then x (top-to-bottom, left-to-right).
 *
 * @param gridItems - Array of grid layout items
 * @returns Map from widget ID to position (0-based)
 */
export function getPositionMapFromGrid(
  gridItems: GridLayoutItem[],
): Map<string, number> {
  // Sort grid items by y position first, then by x
  const sortedGrid = [...gridItems].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  // Create position map
  const positionMap = new Map<string, number>();
  sortedGrid.forEach((item, index) => {
    positionMap.set(item.i, index);
  });

  return positionMap;
}

/**
 * Creates a map from widget ID to grid item for quick lookup.
 *
 * @param gridItems - Array of grid layout items
 * @returns Map from widget ID to grid item
 */
export function getGridItemMap(
  gridItems: GridLayoutItem[],
): Map<string, GridLayoutItem> {
  const gridMap = new Map<string, GridLayoutItem>();
  gridItems.forEach((item) => {
    gridMap.set(item.i, item);
  });
  return gridMap;
}
